import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, RefreshCw, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { getDownloadUrl, listAttachments, softDeleteAttachment } from '@/features/attachments';

import { OpenPdfInNewWindow } from './open-pdf-new-window';
import {
  INVOICE_PDF_CATEGORY,
  invoicePdfAttachmentsKey,
  newestInvoicePdfAttachment,
  performInvoicePdfUpload,
  validatePdf,
} from '../lib/invoice-pdf-upload';

export { invoicePdfAttachmentsKey };

function usePdfQueries(invoiceId: string) {
  const listQuery = useQuery({
    queryKey: invoicePdfAttachmentsKey(invoiceId),
    queryFn: () =>
      listAttachments({
        entityType: 'invoice',
        entityId: invoiceId,
        category: INVOICE_PDF_CATEGORY,
      }),
  });
  const attachment = newestInvoicePdfAttachment(listQuery.data);
  const previewQuery = useQuery({
    queryKey: ['invoice-pdf-url', attachment?.file_id ?? null],
    enabled: attachment !== null,
    queryFn: async () => (await getDownloadUrl(attachment?.file_id ?? '', 600)).url,
  });
  return {
    attachment,
    previewUrl: previewQuery.data ?? null,
    previewLoading: previewQuery.isLoading,
  };
}

function useInvoicePdfPanel(invoiceId: string, onDocumentChanged?: () => void) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { attachment, previewUrl, previewLoading } = usePdfQueries(invoiceId);
  // The BL reads carry the uploaded-PDF flag, so they must flip with the attachment.
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: invoicePdfAttachmentsKey(invoiceId) }),
      qc.invalidateQueries({ queryKey: ['bl-order-invoices'] }),
      qc.invalidateQueries({ queryKey: ['bl-order-detail'] }),
    ]);

  const run = async (action: () => Promise<void>, ok: string, fail: string): Promise<boolean> => {
    setBusy(true);
    try {
      await action();
      toast.success(ok);
      await refresh();
      return true;
    } catch {
      toast.error(fail);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File) => {
    const err = validatePdf(file);
    if (err !== null) {
      toast.error(err);
      return;
    }
    const ok = await run(
      () => performInvoicePdfUpload(invoiceId, file, attachment),
      'Invoice PDF uploaded.',
      'Failed to upload the invoice PDF.',
    );
    if (ok) onDocumentChanged?.();
  };

  const remove = async () => {
    if (attachment === null) return;
    const ok = await run(
      () => softDeleteAttachment(attachment.attachment_id),
      'Invoice PDF removed.',
      'Failed to remove the invoice PDF.',
    );
    if (ok) onDocumentChanged?.();
  };

  return { attachment, previewUrl, previewLoading, busy, upload, remove };
}

function PreviewArea({
  url,
  loading,
  hasAttachment,
  emptyMessage,
}: Readonly<{
  url: string | null;
  loading: boolean;
  hasAttachment: boolean;
  emptyMessage: string;
}>) {
  if (!hasAttachment) {
    return (
      <div
        className="bg-muted/30 text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 rounded-md border border-dashed p-6 text-center text-sm"
        data-testid="invoice-pdf-empty"
      >
        <FileText className="size-8" aria-hidden />
        <p>{emptyMessage}</p>
      </div>
    );
  }
  if (loading || url === null) {
    return (
      <div className="bg-muted/30 text-muted-foreground flex flex-1 items-center justify-center rounded-md border text-sm">
        <Loader2 className="mr-2 size-4 animate-spin" aria-hidden /> Loading preview…
      </div>
    );
  }
  return (
    <div className="flex flex-1 flex-col gap-2">
      <iframe
        src={url}
        title="Invoice PDF preview"
        className="min-h-[18rem] flex-1 rounded-md border"
        data-testid="invoice-pdf-preview"
      />
      <OpenPdfInNewWindow url={url} />
    </div>
  );
}

function UploadIcon({ busy, hasAttachment }: Readonly<{ busy: boolean; hasAttachment: boolean }>) {
  if (busy) return <Loader2 className="size-4 animate-spin" aria-hidden />;
  if (hasAttachment) return <RefreshCw className="size-4" aria-hidden />;
  return <Upload className="size-4" aria-hidden />;
}

function PanelActions({
  busy,
  hasAttachment,
  onUpload,
  onDelete,
}: Readonly<{
  busy: boolean;
  hasAttachment: boolean;
  onUpload: () => void;
  onDelete: () => void;
}>) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        data-testid="invoice-pdf-upload"
        onClick={onUpload}
      >
        <UploadIcon busy={busy} hasAttachment={hasAttachment} />
        {hasAttachment ? 'Re-upload' : 'Upload PDF'}
      </Button>
      {hasAttachment ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy}
          className="text-destructive"
          data-testid="invoice-pdf-delete"
          onClick={onDelete}
        >
          <Trash2 className="size-4" aria-hidden /> Delete
        </Button>
      ) : null}
    </div>
  );
}

export interface InvoicePdfPanelProps {
  readonly invoiceId: string;
  // Fired after a successful upload, re-upload, or delete. The correction
  // dialog uses it to flag that the document was replaced so its audit event
  // records the change alongside any number correction.
  readonly onDocumentChanged?: (() => void) | undefined;
  // View-only: no Upload / Re-upload / Delete. Used on the sell side, where
  // the document is generated at issue — a legacy upload stays visible but is
  // not editable.
  readonly readOnly?: boolean | undefined;
}

// Right-pane Invoice-PDF panel for the edit dialog (Launchpad parity): preview
// the attached PDF + Upload / Re-upload / Delete, backed by the Files pipeline.
export function InvoicePdfPanel({ invoiceId, onDocumentChanged, readOnly }: InvoicePdfPanelProps) {
  const panel = useInvoicePdfPanel(invoiceId, onDocumentChanged);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasAttachment = panel.attachment !== null;

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void panel.upload(file);
  };

  return (
    <section aria-labelledby="invoice-pdf-heading" className="flex min-h-0 flex-1 flex-col gap-3">
      <h3 id="invoice-pdf-heading" className="text-sm font-semibold">
        Invoice PDF
      </h3>
      <PreviewArea
        url={panel.previewUrl}
        loading={panel.previewLoading}
        hasAttachment={hasAttachment}
        emptyMessage={
          readOnly === true
            ? 'This document is generated by Foundry when it is issued.'
            : 'No invoice PDF attached yet.'
        }
      />
      {readOnly !== true && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            data-testid="invoice-pdf-file-input"
            onChange={onPick}
          />
          <PanelActions
            busy={panel.busy}
            hasAttachment={hasAttachment}
            onUpload={() => {
              fileInputRef.current?.click();
            }}
            onDelete={() => {
              void panel.remove();
            }}
          />
        </>
      )}
    </section>
  );
}
